import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Collapse,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle as HealthyIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import billingService from '../../services/billingService';
import HelpIcon from '../../components/help/HelpIcon';

const severityColor = (s) => ({
  CRITICAL: 'error',
  WARNING: 'warning',
  INFO: 'info',
  HEALTHY: 'success',
}[s] || 'info');

/**
 * Operational health banner — top-of-page summary of anything currently
 * broken in the ClickHouse subsystem. Polls the backend every 30 s.
 *
 * <p>When all is well, the banner is a thin green "HEALTHY" chip. When
 * issues exist, it expands into a warning/error alert with the top
 * message visible and a chevron to see the full list.
 */
const ClickHouseHealthBanner = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await billingService.operationalHealth();
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Health check failed');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [load]);

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Health probe failed: {error}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        Loading operational health…
      </Alert>
    );
  }

  const overall = data.overallStatus || 'HEALTHY';
  const color = severityColor(overall);
  const issues = Array.isArray(data.issues) ? data.issues : [];

  if (overall === 'HEALTHY') {
    // Keep a slim "all good" reassurance line — makes it obvious the
    // check is live and running, which is worth more than saving 30 px.
    return (
      <Alert
        severity="success"
        icon={<HealthyIcon />}
        sx={{ mb: 2, py: 0.5 }}
        action={
          <Tooltip title="Refresh health">
            <IconButton size="small" onClick={load} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        }
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">All operational checks passing.</Typography>
          <HelpIcon docKey="operational_health" />
        </Box>
      </Alert>
    );
  }

  const top = issues[0];
  return (
    <Alert
      severity={color}
      sx={{ mb: 2 }}
      action={
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={load} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={() => setExpanded(e => !e)}>
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Stack>
      }
    >
      <AlertTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {overall === 'CRITICAL' ? 'Critical operational issue' : overall === 'WARNING' ? 'Operational warning' : 'Operational note'}
          </Typography>
          {(data.critical || 0) > 0 && <Chip size="small" color="error" label={`${data.critical} critical`} />}
          {(data.warning || 0) > 0 && <Chip size="small" color="warning" label={`${data.warning} warning`} />}
          {(data.info || 0) > 0 && <Chip size="small" color="info" label={`${data.info} info`} />}
          <HelpIcon docKey="operational_health" />
        </Stack>
      </AlertTitle>
      <Typography variant="body2">
        {top?.message || 'See list below.'}
      </Typography>
      <Collapse in={expanded}>
        <Box component="ul" sx={{ mt: 1, pl: 2, mb: 0 }}>
          {issues.map((i, idx) => (
            <li key={idx}>
              <Typography variant="body2">
                <Chip size="small" color={severityColor(i.severity)} label={i.severity}
                      sx={{ mr: 1, fontSize: 10, height: 18 }} />
                <strong>[{i.category}]</strong> {i.message}
                {i.where && (
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    — {i.where}
                  </Typography>
                )}
              </Typography>
            </li>
          ))}
        </Box>
      </Collapse>
    </Alert>
  );
};

export default ClickHouseHealthBanner;
